import { curatedThumb } from "@softmax/web-features/replay/art";

import { readPublishedStorePage } from "../store-page.data";
import { type StorePagePublic } from "@/features/store-editor/records";

import { previewHeroArt } from "@/features/preview/content/preview-content";

import { readCoworldManifest } from "../metadata/read-coworld-manifest";

import { manifestRulesDocPage } from "../metadata/manifest-facts";

import { readParticipationGuide } from "../rules/participation-guide.data";
import { readRulesContent } from "../rules/preview-rules.data";
import { readExternalRulesDoc } from "../rules/external-rules.data";

import { resolveMedia } from "../media/preview-media.data";

import type { ResolvedPreviewLeague } from "../preview-league.data";
import { readPreviewCompetition } from "./preview-competition.data";
import { previewHeroMedia } from "../media/preview-hero-media";
export async function readPreviewResources({
  signal,
  resolved,
  includeDetails,
}: {
  signal?: AbortSignal | undefined;
  resolved: ResolvedPreviewLeague;
  includeDetails: boolean;
}) {
  const {
    league,
    leagueSegment,
    coworldSlug,
    coworldName,
    coworldLeagues,
    addresses,
  } = resolved;
  const coworldId =
    league.game.coworld_id ?? league.game.canonical_coworld_id ?? null;
  const coworldWikiName = league.game.coworld_name ?? coworldSlug;
  const storePagePromise: Promise<StorePagePublic | null> = leagueSegment
    ? Promise.resolve(null)
    : readPublishedStorePage({
        signal,
        slug: coworldSlug,
      });
  const [
    { state, board },
    coworldRead,
    wikiRulesPage,
    guide,
    storePage,
    media,
  ] = await Promise.all([
    readPreviewCompetition({ signal, resolved, includeDetails }),
    readCoworldManifest({
      signal,
      coworldId,
    }),
    includeDetails
      ? readRulesContent({
          signal,
          wikiName: coworldWikiName,
        })
      : null,
    includeDetails
      ? readParticipationGuide({
          signal,
          leagueId: league.id,
        })
      : "",
    storePagePromise,
    includeDetails
      ? resolveMedia({
          signal,
          params: {
            heroArt: previewHeroArt[coworldSlug] ?? null,
            art: curatedThumb(coworldSlug),
            name: coworldName,
            wikiName: coworldWikiName,
            coworldSlug,
            leagues: coworldLeagues,
            addresses,
            leagueId: league.id,
            storePagePromise,
          },
        })
      : storePagePromise.then((published) =>
          previewHeroMedia({ published, coworldSlug, coworldName }),
        ),
  ]);

  if (coworldRead === "private") return null;

  const manifestRulesCandidate = manifestRulesDocPage(
    coworldRead?.manifest ?? null,
  );
  const rulesPage =
    wikiRulesPage ??
    (includeDetails && manifestRulesCandidate
      ? await readExternalRulesDoc({
          signal,
          candidate: manifestRulesCandidate,
        })
      : null);

  return { state, board, coworldRead, guide, storePage, media, rulesPage };
}
export type PreviewResources = NonNullable<
  Awaited<ReturnType<typeof readPreviewResources>>
>;
